/**
 * AutoLangRedirect — One-shot auto-redirect to /es/* for Spanish-speaking
 * visitors on their first visit to a tenant microsite.
 *
 * Behavior:
 *  1. Runs only on public tenant microsite routes (/s/<sub>/... or root).
 *  2. If the user already has a language preference saved (via language
 *     toggle), NEVER redirects. User choice always wins.
 *  3. If navigator.language starts with "es" and the URL is NOT already
 *     under /es, navigate once to the /es counterpart, then mark it done.
 *  4. Stores a session flag so back-button doesn't re-trigger the redirect.
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useThemeBaseRoot } from "../../hooks/useThemeBase";
import { getRememberedLang, rememberLangChoice } from "./PublicLangToggle";

const SESSION_KEY = "public_lang_autoredirect_done";

export default function AutoLangRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const base = useThemeBaseRoot();

  useEffect(() => {
    try {
      // Respect explicit user choice
      if (getRememberedLang()) return;
      // Only fire once per tab session
      if (sessionStorage.getItem(SESSION_KEY)) return;

      const afterBase = location.pathname.slice(base.length);
      const onEs = afterBase === "/es" || afterBase.startsWith("/es/");
      if (onEs) {
        // User is already on Spanish version → record preference
        rememberLangChoice("es");
        sessionStorage.setItem(SESSION_KEY, "1");
        return;
      }

      // Preview demo routes: don't redirect (no /es counterpart)
      if (base.startsWith("/preview-")) return;

      const navLang = (navigator.language || navigator.userLanguage || "en").toLowerCase();
      if (navLang.startsWith("es")) {
        const target = `${base}/es${afterBase === "/" ? "" : afterBase}${location.search}`;
        sessionStorage.setItem(SESSION_KEY, "1");
        navigate(target, { replace: true });
      } else {
        sessionStorage.setItem(SESSION_KEY, "1");
      }
    } catch {
      /* never break the site over i18n */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
